import React, { useRef } from 'react';
import { emptyState } from '@techchange/pm-specs';
import axios from 'axios';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { v4 as uuid } from 'uuid';

import {
	DragPreview,
	DragSource,
	DropTargetForGrid,
	DRAG_TYPES,
	calculateWeight,
} from '@techchange/dnd';
import { API_BASE_URL, toSlug } from '@techchange/components';

import {
	RESOURCE_TYPES,
	RESOURCE_ASSOCIATED_OBJECT_TYPES,
} from '../../constants';
import {
	calculateResourceItemWidth,
	calculateResourceRowWidth,
} from '../../utils/resources';

import ContentDropdownMenu from './ContentDropdownMenu';
import ContentGridDragPreview from './ContentGridDragPreview';
import ContentGridItem from './ContentGridItem';
import types from './utils/types';

const axiosClient = axios.create({
	baseURL: API_BASE_URL.RESOURCES,
	withCredentials: true,
});

const propTypes = {

	/**
	 * The parent content resource.
	 */
	resource: PropTypes.shape({

		/**
		 * The unique ID of the resource.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.oneOf([
			RESOURCE_TYPES.PAGE,
			RESOURCE_TYPES.MODULE,
			RESOURCE_TYPES.SUBMODULE,
			RESOURCE_TYPES.SUBMODULE_CONTENT,
		]).isRequired,

		/**
		 * The resource title.
		 */
		title: PropTypes.string,

		/**
		 * The resource slug.
		 */
		slug: PropTypes.string,

		/**
		 * The resource url.
		 */
		url: PropTypes.string.isRequired,

		/**
		 * The resource path.
		 */
		path: PropTypes.string.isRequired,

		/**
		 * The type of object that is associated with the parent content resource.
		 *
		 * i.e. A discussion thread, event, or product.
		 *
		 * NOTE: This field is only required for the "submodule-content" root resources,
		 *       as "page" root resources are standalone objects for now.
		 */
		associated_object_type: PropTypes.oneOf([
			RESOURCE_ASSOCIATED_OBJECT_TYPES.DISCUSSION_THREAD,
			RESOURCE_ASSOCIATED_OBJECT_TYPES.EVENT,
			RESOURCE_ASSOCIATED_OBJECT_TYPES.EVENT_STAGE,
			RESOURCE_ASSOCIATED_OBJECT_TYPES.PRODUCT,
		]),

		/* The ID of the object that is associated with the parent content resource.
		 *
		 * i.e. A discussion thread, event, or product.
		 *
		 * NOTE: This field is only required for the "submodule-content" root resources,
		 *       as "page" root resources are standalone objects for now.
		 */
		associated_object_id: PropTypes.string,

		/**
		 * The resource scope.
		 */
		scope: PropTypes.string.isRequired,

		/**
		 * The resource scope ID.
		 */
		scope_id: PropTypes.string.isRequired,

		/**
		 * The resource organization ID.
		 */
		organization_id: PropTypes.string.isRequired,

		/**
		 * The timestamp (if any) of creation for the resource.
		 */
		created_at: PropTypes.string,

		/**
		 * The children resources (if any).
		 */
		children: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The child resource ID.
			 */
			id: PropTypes.string,

		})),

	}).isRequired,

	/**
	 * The rows of child resources to render.
	 */
	gridRows: PropTypes.arrayOf(

		PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The resource ID.
			 */
			id: PropTypes.string.isRequired,

		})).isRequired,

	).isRequired,

	/**
	 * Real-time changes (if any) to the content grid while editing.
	 *
	 * NOTE: gridChanges[resourceID] -> The data that changed for the resource.
	 * NOTE: gridChanges[resourceID:editing-state] -> The initial editing state for the resource.
	 */
	gridChanges: PropTypes.shape({}).isRequired,

	/**
	 * Configuration settings for the resource types to use in Content Builder.
	 */
	config: types.config.isRequired,

	/**
	 * An optional label for the button to toggle open the dropdown menu
	 * to add a new piece of content to the grid.
	 */
	dropdownMenuPlaceholderLabel: PropTypes.string,

	/**
	 * Callback function to handle updating the local state whenever
	 * the parent content resource changes.
	 */
	onChangeResource: PropTypes.func.isRequired,

	/**
	 * Callback function to keep track of real-time changes to the
	 * content grid (i.e. resource width) while editing.
	 */
	onWidthChange: PropTypes.func.isRequired,

};

/**
 * Disables interaction with iframes before drag-and-drop.
 *
 * @param {object} ref  The react ref to the content grid.
 */
const handleDragStart = (ref) => {

	if (ref && ref.current) {

		const iframes = ref.current.getElementsByTagName('iframe');

		for (let i = 0, len = iframes.length; i < len; i++) {

			iframes[i].style['pointer-events'] = 'none';

		}

	}

};

/**
 * Enables interaction with iframes after drag-and-drop.
 *
 * @param {object} ref  The react ref to the content grid.
 */
const handleDragEnd = (ref) => {

	if (ref && ref.current) {

		const iframes = ref.current.getElementsByTagName('iframe');

		for (let i = 0, len = iframes.length; i < len; i++) {

			iframes[i].style.removeProperty('pointer-events');

		}

	}

};

const ContentGridAdmin = (props) => {

	const {
		resource,
		gridRows,
		gridChanges,
		config,
		dropdownMenuPlaceholderLabel,
		onChangeResource,
		...restProps
	} = props;

	// Keep track of the ref for the parent content resource
	const contentRef = useRef(null);

	const children = resource.children || [];

	// Gets the index of a child resource relative to the list of all child resources
	const getIndex = (id) => children.findIndex((child) => child.id === id);

	/**
	 * Checks if a child is being edited and has local changes that affect the grid layout.
	 *
	 * @param   {object} child  The child resource.
	 *
	 * @returns {boolean}       True, if editing and local changes.
	 */
	const isEditingContent = (child) => {

		if (gridChanges[child.id]) {
			return true;
		}

		return false;

	};

	/**
	 * Handles creating a new child resource for the parent content resource.
	 *
	 * @param {object} event  The ReactJS click event.
	 * @param {string} type   The content type.
	 *
	 * NOTE: Pseudo creates the resource by adding to local state, but doesn't persist on the backend.
	 */
	const handleCreateContent = (event, type) => {

		// Create a copy of the parent content resource
		const result = Object.assign({}, resource);

		// Create a copy of the children for the parent content resource
		result.children = result.children ? [...result.children] : [];

		// Create a new ID for the child resource
		const newID = uuid();

		// Create a new slug for the child resource
		const newSlug = toSlug(type, newID);

		// Find the sibling that the child resource will be placed after
		const lastSibling = result.children[result.children.length - 1];

		const after = lastSibling ? lastSibling.weight : null;

		// Find the last row of siblings that child resource will be placed after
		const lastRow = gridRows.length > 0 ? gridRows[gridRows.length - 1] : [];

		// Calculate the weight of the child resource
		const weight = calculateWeight(after, null);

		// Calculate the width of the new piece of content
		const width = calculateResourceItemWidth(lastRow);

		// Add the child resource
		result.children.push({
			id: newID,
			active: true,
			title: '',
			slug: newSlug,
			type,
			path: `${result.path}#${newID}`,
			content: type === RESOURCE_TYPES.SLIDE ? emptyState : null,
			data: type === RESOURCE_TYPES.SLIDE ? null : {},
			width,
			weight,
			scope: result.scope,
			scope_id: result.scope_id,
			organization_id: result.organization_id,
			children: [],
		});

		onChangeResource(result);

	};

	/**
	 * Handles deleting a child resource for the parent content resource.
	 *
	 * @param {object} parent  The parent content resource.
	 * @param {object} data    The child resource to delete.
	 *
	 * @returns {promise} The promise to resolve.
	 */
	const handleDeleteContent = (parent, data) => {

		let promise;

		// Check if we need to actually delete the resource from the backend...
		if (data.created_at) {

			promise = axiosClient.delete(`/${data.id}`, {
				params: {
					scope: resource.scope,
					scope_id: resource.scope_id,
				},
			});

		// Otherwise, it was cancelled before being persisted (i.e. during creation)...
		} else {

			promise = new Promise((resolve) => resolve());

		}

		return promise.then(() => {

			// Create a copy of the parent content resource
			const result = Object.assign({}, resource, {
				children: [...resource.children],
			});

			// Find the index of the child resource to delete
			const index = result.children.findIndex((child) => child.id === data.id);

			// Remove the child resource
			result.children.splice(index, 1);

			onChangeResource(result);

		});

	};

	/**
	 * Handles re-ordering a child resource for the parent content resource.
	 *
	 * @param {string} id        The id of the child resource to move.
	 * @param {object} prevItem  The previous sibling resource (after the re-ordering).
	 * @param {object} nextItem  The next sibling resource (after the re-ordering).
	 *
	 * @returns {promise} The promise to resolve.
	 */
	const handleMoveContent = (id, prevItem, nextItem) => {

		// Find the weight of the previous/next sibling resources
		const prevWeight = prevItem ? prevItem.weight : null;
		const nextWeight = nextItem ? nextItem.weight : null;

		// Calculate the new weight of the child resource to move
		const newWeight = calculateWeight(prevWeight, nextWeight);

		// Create a copy of the parent content resource
		const result = Object.assign({}, resource, {
			children: [...resource.children],
		});

		// Find the index of the child resource to move
		const index = result.children.findIndex((child) => child.id === id);

		// Update the weight of the child resource to move
		result.children[index] = Object.assign({}, result.children[index], {
			weight: newWeight,
		});

		// Re-sort the child resources
		result.children.sort((child1, child2) => child1.weight - child2.weight);

		// Perform the move locally in real-time, so there is no noticeable lag to the user
		onChangeResource(result);

		// Handle any actions needed on drag end (just in case "dragEnd" event doesn't fire)
		handleDragEnd(contentRef);

		// Persist the changes to actually move the resource in the backend
		return axiosClient.patch(`/${id}`, {
			weight: newWeight,
			scope: resource.scope,
			scope_id: resource.scope_id,
		}).catch((error) => {

			// TODO: What to do in case of API failure???? Revert? Error message?
			console.error(error);

		});

	};

	/**
	 * Handles creating/updating a child resource for the parent content resource.
	 *
	 * @param {object} parent  The parent content resource.
	 * @param {object} data    The child resource to create/update.
	 *
	 * @returns {promise} The promise to resolve.
	 */
	const handleSaveContent = (parent, data) => {

		let parentPromise = new Promise((resolve) => resolve({ data: parent }));

		const creatingParent = !parent.created_at;

		// Check if the parent content resource has not yet been persisted in the backend...
		if (creatingParent) {
			parentPromise = axiosClient.post('/', parent);
		}

		return parentPromise.then(({ data: updatedParentResource }) => {

			let childPromise;

			// Check if we are updating an existing child resource...
			if (data.created_at) {

				childPromise = axiosClient.patch(`/${data.id}`, data);

			} else {

				childPromise = axiosClient.post('/', data);

			}

			return childPromise.then(({ data: updatedChildResource }) => {

				// Create a copy of the parent content resource
				const result = Object.assign({}, updatedParentResource, {
					children: updatedParentResource.children ? [...updatedParentResource.children] : [],
				});

				// Find the index of the child resource to update
				const index = result.children.findIndex((child) => child.id === updatedChildResource.id);

				// Update the existing child resource, if any
				if (index >= 0) {
					result.children[index] = updatedChildResource;
				}

				// If parent was newly created, add child resource to children
				if (creatingParent) {
					result.children.push(updatedChildResource);
				}

				onChangeResource(result);

			});

		});

	};

	// Limit drag-and-drop functionality to only this content grid
	const dragType = `${DRAG_TYPES.SUBMODULE_CONTENT}-${resource.id}`;

	return (
		<div ref={contentRef}>
			<DragPreview accept={[dragType]}>
				<ContentGridDragPreview />
			</DragPreview>
			{gridRows.map((row, rowIndex) => (
				<Row
					key={row.map((child) => child.id).join('-')}
					className="row-of-content-resources">
					{/* Place a drop target over the entire row to display
						top/bottom drop target locations */}
					<div
						id={`grid-${resource.id}-drop-target-container-for-row-${rowIndex}`}
						className={
							`tc-grid-drop-target-container-for-row
							${rowIndex === 0 ? 'tc-grid-first-row' : ''}
						`}>
					</div>
					{/* Loop through the row of content */}
					{row.map((child, colIndex) => (
						<Col
							key={`${child.id}-${child.width}`}
							sm={child.width}
							xs="12"
							className="content-container position-relative">
							{/* Place a drop target over the piece of content to handle drag-and-drop */}
							<DropTargetForGrid
								type={dragType}
								currentItem={child}
								prevItem={children[getIndex(child.id) - 1]}
								nextItem={children[getIndex(child.id) + 1]}
								currentRow={row}
								prevRow={gridRows[rowIndex - 1]}
								nextRow={gridRows[rowIndex + 1]}
								positioning={{
									firstRow: rowIndex === 0,
									lastRow: rowIndex === gridRows.length - 1,
									firstInRow: colIndex === 0,
									lastInRow: colIndex === row.length - 1 && calculateResourceRowWidth(row) === 12,
									dropTargetForRowID:
										`grid-${resource.id}-drop-target-container-for-row-${rowIndex}`,
								}} />
							<DragSource
								type={dragType}
								item={child}
								onMove={handleMoveContent}>
								<ContentGridItem
									id={child.id}
									resource={child}
									parent={resource}
									isEditingOnInitialLoad={isEditingContent(child)}
									isAdmin
									onDragStart={() => handleDragStart(contentRef)}
									onDragEnd={() => handleDragEnd(contentRef)}
									onDeleteResource={handleDeleteContent}
									onSaveResource={handleSaveContent}
									{...restProps} />
							</DragSource>
						</Col>
					))}
					{/* Use a duplicate drop target for the last item to fill up
						any remaining space in the row (if any) */}
					{calculateResourceRowWidth(row) < 12 &&
						<Col
							sm={12 - calculateResourceRowWidth(row)}
							className="content-container position-relative d-none d-sm-block">
							<DropTargetForGrid
								type={dragType}
								currentItem={row[row.length - 1]}
								prevItem={children[getIndex(row[row.length - 1].id) - 1]}
								nextItem={children[getIndex(row[row.length - 1].id) + 1]}
								currentRow={row}
								prevRow={gridRows[rowIndex - 1]}
								nextRow={gridRows[rowIndex + 1]}
								positioning={{
									firstRow: rowIndex === 0,
									lastRow: rowIndex === gridRows.length - 1,
									firstInRow: false,
									lastInRow: false,
									dropTargetForRowID:
										`grid-${resource.id}-drop-target-container-for-row-${rowIndex}`,
									isEmptySpace: true,
								}} />
						</Col>
					}
				</Row>
			))}
			<ContentDropdownMenu
				config={config}
				placeholderLabel={dropdownMenuPlaceholderLabel}
				onCreate={handleCreateContent} />
		</div>
	);

};

ContentGridAdmin.propTypes = propTypes;

export default ContentGridAdmin;
